import React, { useState, useEffect, useCallback } from 'react';
import { getUnidades, deleteUnidad } from '../../../api/unidades';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, TextField, CircularProgress, TablePagination, Box } from '@mui/material';
import { Edit, Delete, Visibility, Add, Update } from '@mui/icons-material';
import CreateUnidad from './_Create';
import EditUnidad from './_Edit';
import ViewUnidad from './_ViewById';
import { ConfirmDelete } from '../../../components/ConfirmDelete';
import { useAuth } from '../../../context/auth';

const Unidades = () => {
  const [unidades, setUnidades] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [selectedUnidad, setSelectedUnidad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { permisos } = useAuth();
  const { Prm_Config_Unid_Crear, Prm_Config_Unid_Modificar, Prm_Config_Unid_Eliminar } = permisos;
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const handleOpenDeleteConfirm = useCallback((id: number) => {
    setDeleteId(id);
    setOpenConfirmDelete(true);
  }, []);

  const handleCloseDeleteConfirm = useCallback(() => {
    setOpenConfirmDelete(false);
    setDeleteId(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deleteId !== null) {
      await deleteUnidad(deleteId);
      fetchUnidades();
      handleCloseDeleteConfirm();
    }
  }, [deleteId, handleCloseDeleteConfirm]);

  useEffect(() => {
    fetchUnidades();
  }, []);

  const fetchUnidades = useCallback(async () => {
    setLoading(true);
    const response = await getUnidades();
    if (response.isOk) {
      setUnidades(response.data);
    }
    setLoading(false);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const filteredUnidades = unidades.filter((unidad: any) => unidad.Un_Nombre.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div style={{ marginTop: '5px' }}>
      {loading ? (
        <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
          <CircularProgress />
        </Box>
      ) : (
        <div style={{ marginTop: '5px' }}>
          <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
            <TextField label='Buscar' value={searchTerm} onChange={handleSearchChange} fullWidth size='small' />
            {Prm_Config_Unid_Crear === 1 && (
              <IconButton onClick={() => setOpenCreate(true)} title='Nuevo' className='tool-btn'>
                <Add color='success' />
              </IconButton>
            )}
            <IconButton onClick={fetchUnidades} title='Actualizar' className='tool-btn'>
              <Update />
            </IconButton>
          </Box>
          <TableContainer component={Paper}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUnidades.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((unidad: any) => (
                  <TableRow key={unidad.Un_Id}>
                    <TableCell>{unidad.Un_Id}</TableCell>
                    <TableCell>{unidad.Un_Nombre}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setSelectedUnidad(unidad);
                          setOpenView(true);
                        }}>
                        <Visibility color='info' />
                      </IconButton>
                      {Prm_Config_Unid_Modificar === 1 && (
                        <IconButton
                          onClick={() => {
                            setSelectedUnidad(unidad);
                            setOpenEdit(true);
                          }}>
                          <Edit color='primary' />
                        </IconButton>
                      )}
                      {Prm_Config_Unid_Eliminar === 1 && (
                        <IconButton onClick={() => handleOpenDeleteConfirm(unidad.Un_Id)} title='Eliminar'>
                          <Delete color='error' />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component='div' count={filteredUnidades.length} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleChangeRowsPerPage} />
        </div>
      )}

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)}>
        <DialogTitle>Crear Unidad</DialogTitle>
        <DialogContent>
          <CreateUnidad
            onClose={() => {
              setOpenCreate(false);
              fetchUnidades();
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>Editar Unidad</DialogTitle>
        <DialogContent>
          <EditUnidad
            unidad={selectedUnidad}
            onClose={() => {
              setOpenEdit(false);
              fetchUnidades();
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={openView} onClose={() => setOpenView(false)}>
        <DialogTitle>Ver Unidad</DialogTitle>
        <DialogContent>
          <ViewUnidad unidad={selectedUnidad} onClose={() => setOpenView(false)} />
        </DialogContent>
      </Dialog>

      <ConfirmDelete open={openConfirmDelete} onClose={handleCloseDeleteConfirm} onConfirm={handleConfirmDelete} message='¿Estás seguro de que deseas eliminar esta unidad?' />
    </div>
  );
};

export default Unidades;
